import { useMemo, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Check,
  Filter,
  Play,
  Search,
  ShieldCheck,
  Sparkles,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import type { OrgPluginListResponse } from '@reflex/shared';
import { request } from '@reflex/ui/client/http';
import { useActiveOrgId } from '@reflex/ui/client/api/org-id';
import { orgPluginsQueryKey } from '@reflex/ui/client/hooks';
import { PluginIcon } from '@reflex/ui/components/shared/PluginIcon';
import { Spinner } from '@reflex/ui/components/shared/Spinner';
import { Button } from '@reflex/ui/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@reflex/ui/components/ui/dialog';
import { Input } from '@reflex/ui/components/ui/input';
import { SectionLabel } from '@reflex/ui/components/layout/Typography';
import { cn } from '@reflex/ui/lib/utils';
import { pluginDisplayName } from '../../shared/templates';
import {
  SPEC_KINDS,
  SPEC_KIND_LABELS,
  buildSpecifications,
  connectorsFromSpecifications,
  filterSpecifications,
  groupSpecifications,
  type SpecKind,
  type Specification,
} from '../../shared/specifications';
import { useFlowActionCatalog, useFlowEventCatalog } from '../useFlows';

/** How many specs to show under SUGGESTED when no connector/kind filter is on. */
const SUGGESTED_LIMIT = 4;

export const SPEC_KIND_ICONS: Record<SpecKind, { icon: LucideIcon; bg: string; color: string }> = {
  trigger: { icon: Play, bg: 'bg-status-accent-orange/15', color: 'text-status-accent-orange' },
  action: { icon: Zap, bg: 'bg-muted', color: 'text-muted-foreground' },
  agent_task: {
    icon: Sparkles,
    bg: 'bg-status-accent-purple/15',
    color: 'text-status-accent-purple',
  },
  filter: { icon: Filter, bg: 'bg-status-accent-sky/15', color: 'text-status-accent-sky' },
  approval: { icon: ShieldCheck, bg: 'bg-status-success/15', color: 'text-status-success' },
};

export interface SpecificationPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Currently confirmed selection (chips on the generate box). */
  value: Specification[];
  /** Called when the user confirms; replaces the parent selection. */
  onConfirm: (specs: Specification[]) => void;
}

/**
 * Two-pane modal for picking specifications that refine the AI generate
 * prompt on `/automations/new`. Connectors come from the org's installed
 * plugins that contribute catalog entries; kinds chip-filter the list;
 * multi-select checkboxes feed the confirm footer.
 */
export function SpecificationPicker({
  open,
  onOpenChange,
  value,
  onConfirm,
}: SpecificationPickerProps) {
  const orgId = useActiveOrgId();
  const { data: events, isLoading: eventsLoading, isError: eventsError } = useFlowEventCatalog();
  const {
    data: actions,
    isLoading: actionsLoading,
    isError: actionsError,
  } = useFlowActionCatalog();
  const {
    data: orgPlugins,
    isLoading: pluginsLoading,
    isError: pluginsError,
  } = useQuery({
    queryKey: orgPluginsQueryKey(orgId),
    queryFn: () =>
      request<OrgPluginListResponse>(`/organizations/${encodeURIComponent(orgId!)}/plugins`),
    enabled: !!orgId && open,
    staleTime: 60_000,
  });

  const [selectedConnector, setSelectedConnector] = useState<string | null>(null);
  const [activeKinds, setActiveKinds] = useState<Set<SpecKind>>(() => new Set());
  const [search, setSearch] = useState('');
  // Draft selection inside the modal; committed only on confirm.
  const [draft, setDraft] = useState<Map<string, Specification>>(() => new Map());

  // Reset draft + filters whenever the dialog opens so it mirrors `value`.
  useEffect(() => {
    if (!open) return;
    setDraft(new Map(value.map((s) => [s.id, s])));
    setSelectedConnector(null);
    setActiveKinds(new Set());
    setSearch('');
  }, [open, value]);

  const installedByName = useMemo(() => {
    const map = new Map<string, { icon?: string; label: string }>();
    if (!orgPlugins) return map;
    for (const row of [...orgPlugins.installed, ...orgPlugins.system]) {
      if (row.unavailableReason) continue;
      map.set(row.pluginName, {
        icon: row.icon,
        label: pluginDisplayName(row.pluginName),
      });
    }
    return map;
  }, [orgPlugins]);

  const allSpecs = useMemo(
    () => buildSpecifications({ events: events ?? [], actions: actions ?? [] }),
    [events, actions],
  );

  // Installed-only: keep connector-scoped specs whose plugin is installed,
  // plus every generic. When the org-plugins query hasn't resolved (or
  // failed), fall open on the catalogs rather than blanking the modal.
  const availableSpecs = useMemo(() => {
    if (installedByName.size === 0) return allSpecs;
    return allSpecs.filter((s) => s.connector == null || installedByName.has(s.connector));
  }, [allSpecs, installedByName]);

  const connectors = useMemo(() => {
    const names = connectorsFromSpecifications(availableSpecs);
    return names.map((name) => ({
      name,
      label: installedByName.get(name)?.label ?? pluginDisplayName(name),
      icon: installedByName.get(name)?.icon ?? `plugin-icon-${name}.png`,
    }));
  }, [availableSpecs, installedByName]);

  const hasActiveFilter = selectedConnector != null || activeKinds.size > 0 || search.trim() !== '';

  const filtered = useMemo(() => {
    const result = filterSpecifications(availableSpecs, {
      connector: selectedConnector,
      kinds: activeKinds,
      search,
    });
    if (!hasActiveFilter) return result.slice(0, SUGGESTED_LIMIT);
    return result;
  }, [availableSpecs, selectedConnector, activeKinds, search, hasActiveFilter]);

  const groups = useMemo(() => {
    if (!hasActiveFilter) {
      return [{ key: 'suggested', heading: 'SUGGESTED', entries: filtered }];
    }
    return groupSpecifications(filtered);
  }, [filtered, hasActiveFilter]);

  const selectedCount = draft.size;
  const catalogLoading = eventsLoading || actionsLoading || (!!orgId && pluginsLoading);
  const catalogError = eventsError || actionsError || pluginsError;

  const toggleKind = (kind: SpecKind) => {
    setActiveKinds((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });
  };

  const toggleSpec = (spec: Specification) => {
    setDraft((prev) => {
      const next = new Map(prev);
      if (next.has(spec.id)) next.delete(spec.id);
      else next.set(spec.id, spec);
      return next;
    });
  };

  const handleConfirm = () => {
    onConfirm([...draft.values()]);
    onOpenChange(false);
  };

  const handleCancel = () => onOpenChange(false);

  const selectionCountFor = (connector: string) =>
    [...draft.values()].filter((s) => s.connector === connector).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="flex h-[min(640px,85vh)] max-w-[calc(100vw-2rem)] gap-0 overflow-hidden p-0 sm:max-w-5xl"
        data-testid="specification-picker"
      >
        <DialogTitle className="sr-only">Add Trigger, Connectors, and Specs</DialogTitle>
        <DialogDescription className="sr-only">
          Pick triggers, actions, and node types from installed connectors to refine the generate
          prompt.
        </DialogDescription>

        {/* Left: Connectors */}
        <aside className="flex w-[154px] shrink-0 flex-col overflow-y-auto border-r bg-muted p-2">
          <div className="px-2 pb-2 pt-3">
            <SectionLabel as="span">Connectors</SectionLabel>
          </div>
          {connectors.map((connector) => {
            const isActive = selectedConnector === connector.name;
            const count = selectionCountFor(connector.name);
            return (
              <button
                key={connector.name}
                type="button"
                onClick={() =>
                  setSelectedConnector((prev) => (prev === connector.name ? null : connector.name))
                }
                aria-pressed={isActive}
                data-testid={`spec-connector-${connector.name}`}
                className={cn(
                  'flex w-full items-center gap-2.5 rounded-lg p-2 text-left transition-colors',
                  isActive ? 'bg-background' : 'hover:bg-background/60',
                )}
              >
                <PluginIcon
                  icon={connector.icon}
                  size="sm"
                  className="size-[26px] rounded"
                  alt=""
                />
                <span className="min-w-0 flex-1 truncate text-xs font-semibold text-foreground">
                  {connector.label}
                </span>
                {count > 0 && (
                  <span
                    className="flex size-4 shrink-0 items-center justify-center rounded-full bg-status-success text-[10px] font-semibold text-background"
                    aria-label={`${count} selected`}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
          {!catalogLoading && connectors.length === 0 && (
            <p className="px-2 py-4 text-[11px] text-muted-foreground">
              No connectors installed. Install an integration to add triggers and actions.
            </p>
          )}
        </aside>

        {/* Right: Specifications */}
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4 py-2">
            <div>
              <div className="p-2">
                <SectionLabel as="span">Specifications</SectionLabel>
              </div>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {SPEC_KINDS.map((kind) => {
                  const active = activeKinds.has(kind);
                  const style = SPEC_KIND_ICONS[kind];
                  const Icon = style.icon;
                  return (
                    <button
                      key={kind}
                      type="button"
                      onClick={() => toggleKind(kind)}
                      aria-pressed={active}
                      data-testid={`spec-kind-${kind}`}
                      className={cn(
                        'inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                        active
                          ? 'border-status-success bg-status-success/15 text-foreground dark:bg-status-success/25'
                          : 'border-border bg-background text-foreground hover:bg-muted/50',
                      )}
                    >
                      <span
                        className={cn('flex size-4 items-center justify-center rounded', style.bg)}
                      >
                        <Icon className={cn('size-2', style.color)} aria-hidden />
                      </span>
                      {SPEC_KIND_LABELS[kind]}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="relative">
              <Search
                className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search actions…"
                className="h-9 border-0 bg-muted pl-8 text-xs shadow-none focus-visible:ring-0"
                aria-label="Search specifications"
                data-testid="spec-search"
              />
            </div>

            <div className="flex flex-col gap-1">
              {catalogLoading && (
                <div className="flex items-center justify-center py-12 text-muted-foreground">
                  <Spinner size="sm" />
                </div>
              )}
              {catalogError && !catalogLoading && (
                <p className="px-2 py-8 text-center text-xs text-status-error">
                  Couldn&apos;t load specifications. Try again.
                </p>
              )}
              {!catalogLoading && !catalogError && groups.every((g) => g.entries.length === 0) && (
                <p className="px-2 py-8 text-center text-xs text-muted-foreground">
                  No specifications match.
                </p>
              )}
              {!catalogLoading &&
                !catalogError &&
                groups.map((group) =>
                  group.entries.length === 0 ? null : (
                    <div key={group.key} data-testid={`spec-group-${group.key}`}>
                      <div className="flex items-center justify-between p-2">
                        <SectionLabel as="span">{group.heading}</SectionLabel>
                        {group.key !== 'suggested' && (
                          <span className="text-[10px] text-muted-foreground">
                            {group.entries.length}{' '}
                            {group.entries.length === 1 ? 'result' : 'results'}
                          </span>
                        )}
                      </div>
                      <ul className="flex flex-col">
                        {group.entries.map((spec) => {
                          const checked = draft.has(spec.id);
                          return (
                            <li key={spec.id}>
                              <button
                                type="button"
                                role="checkbox"
                                aria-checked={checked}
                                onClick={() => toggleSpec(spec)}
                                data-testid={`spec-row-${spec.id}`}
                                className="flex w-full items-center gap-2.5 rounded-lg p-2 text-left transition-colors hover:bg-muted/60"
                              >
                                <span
                                  aria-hidden
                                  className={cn(
                                    'flex size-4 shrink-0 items-center justify-center rounded border transition-colors',
                                    checked
                                      ? 'border-status-success bg-status-success text-background'
                                      : 'border-foreground/80 bg-transparent',
                                  )}
                                >
                                  {checked && <Check className="size-3" strokeWidth={3} />}
                                </span>
                                <PluginIcon
                                  icon={
                                    spec.connector
                                      ? (installedByName.get(spec.connector)?.icon ??
                                        `plugin-icon-${spec.connector}.png`)
                                      : null
                                  }
                                  size="sm"
                                  className="size-6 rounded"
                                  alt=""
                                />
                                <span className="min-w-0 flex-1">
                                  <span className="block truncate text-xs font-semibold text-foreground">
                                    {spec.label}
                                  </span>
                                  <span className="block truncate text-xs text-muted-foreground">
                                    {spec.description}
                                  </span>
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ),
                )}
            </div>
          </div>

          {selectedCount > 0 && (
            <div
              className="flex items-center justify-between gap-3 border-t px-4 py-3"
              data-testid="spec-footer"
            >
              <span className="text-xs text-muted-foreground">{selectedCount} selected</span>
              <div className="flex items-center gap-2">
                <Button type="button" variant="ghost" size="sm" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button type="button" size="sm" onClick={handleConfirm} data-testid="spec-confirm">
                  Add {selectedCount} {selectedCount === 1 ? 'Specification' : 'Specifications'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
